import { useEffect, useMemo, useState } from 'react'
import type { CfoAnalyticsState, QuickBooksCfoCoreBootstrapResponse, ServerApiClient } from '../../../data/ServerApiClient'
import type { CfoShellCacheState } from './CfoPageShell'
import { trimStr } from './cfoFormat'

const CFO_BOOTSTRAP_CACHE_TTL_MS = 60_000

type CfoBootstrapCacheEntry = {
  value: QuickBooksCfoCoreBootstrapResponse | null
  promise: Promise<QuickBooksCfoCoreBootstrapResponse | null> | null
  error: any | null
  updatedAt: number
}

const cfoBootstrapCache = new Map<string, CfoBootstrapCacheEntry>()

export function invalidateCfoBootstrapCache(workbookId?: string | null) {
  const wb = trimStr(workbookId)
  if (wb) {
    cfoBootstrapCache.delete(wb)
    return
  }
  cfoBootstrapCache.clear()
}

const CFO_ANALYTICS_STATES: ReadonlySet<string> = new Set<CfoAnalyticsState>([
  'ready',
  'stale_showing_previous',
  'needs_report_update',
  'binding_setup',
])

/**
 * The bootstrap's analytics state. Reading the bootstrap never starts work, so
 * nothing here may be inferred from `cfo_analytics_status` prose (a
 * "prepare"/"refresh" word in a status message is not a running job). Older
 * servers and adapted accounting bootstraps omit the explicit state; derive it
 * from readiness alone.
 */
export function cfoAnalyticsState(bootstrap: QuickBooksCfoCoreBootstrapResponse): CfoAnalyticsState {
  const explicit = trimStr(bootstrap.cfo_analytics_state)
  if (CFO_ANALYTICS_STATES.has(explicit)) return explicit as CfoAnalyticsState
  if (bootstrap.binding_setup) return 'binding_setup'
  if (bootstrap.cfo_analytics_ready === false) return 'needs_report_update'
  const status = trimStr(bootstrap.cfo_analytics_status)
  if (bootstrap.cfo_analytics_ready && status && status !== 'ready') return 'stale_showing_previous'
  return 'ready'
}

export function loadCfoBootstrapCached(
  appApi: ServerApiClient,
  workbookId: string | null,
): Promise<QuickBooksCfoCoreBootstrapResponse | null> {
  const wb = trimStr(workbookId)
  if (!wb) return Promise.resolve(null)

  const now = Date.now()
  const cached = cfoBootstrapCache.get(wb)
  if (cached && cached.value && now - cached.updatedAt < CFO_BOOTSTRAP_CACHE_TTL_MS) {
    return Promise.resolve(cached.value)
  }

  const entry = cached || { value: null, promise: null, error: null, updatedAt: 0 }
  if (!entry.promise) {
    const loadBootstrap: () => Promise<QuickBooksCfoCoreBootstrapResponse> = typeof (appApi as any).getCfoCoreBootstrap === 'function'
      ? () => (appApi as any).getCfoCoreBootstrap(wb) as Promise<QuickBooksCfoCoreBootstrapResponse>
      : () => appApi.getQuickBooksCfoCoreBootstrap(wb)
    entry.promise = loadBootstrap()
      .then((boot: QuickBooksCfoCoreBootstrapResponse) => {
        const firstBuildCompleted = entry.value?.cfo_analytics_ready === false && boot.cfo_analytics_ready === true
        entry.value = boot
        entry.error = null
        entry.updatedAt = Date.now()
        if (firstBuildCompleted && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('fg:cfo-materialization-complete', { detail: { workbookId: wb, source: 'bootstrap' } }))
        }
        return boot
      })
      .catch((error: unknown) => {
        entry.value = null
        entry.error = error
        entry.updatedAt = Date.now()
        return null
      })
      .finally(() => {
        entry.promise = null
      })
    cfoBootstrapCache.set(wb, entry)
  }
  return entry.promise as Promise<QuickBooksCfoCoreBootstrapResponse | null>
}

/**
 * Whether Pro-tier surfaces (modeled cash actions, pacing vs forecast) are
 * available for this workbook's tenant. Default permissive: a missing plan
 * payload means a legacy tenant, an older server, or bootstrap still loading —
 * the server independently enforces the hard gates.
 */
export function cfoProFeaturesAvailable(
  // Structural, like `cfoJobCostingAvailable`: the shell resolves navigation from either the CFO
  // core bootstrap or the accounting workbook bootstrap, and only the plan block matters here.
  bootstrap: { plan?: { pro_features_available?: boolean | null } | null } | null | undefined,
): boolean {
  const plan = bootstrap?.plan
  if (!plan) return true
  return plan.pro_features_available !== false
}

/**
 * Optional modules are absent until the bootstrap explicitly proves them
 * ready. Older servers and failed bootstrap reads therefore hide Job finance
 * instead of sending a user into an unavailable report endpoint.
 */
export function cfoJobCostingAvailable(
  bootstrap: {
    capabilities?: Record<string, boolean> | null
    capability_details?: Record<string, { available: boolean }> | null
  } | null | undefined,
): boolean {
  if (bootstrap?.capabilities?.job_costing !== true) return false
  const detail = bootstrap.capability_details?.job_costing
  return !detail || detail.available === true
}

/**
 * Map bootstrap payload → topbar cache pill (shared across all calm pages).
 * The pill describes what is served. Progress of a durable report update is
 * owned by the materialization status banner, which polls only while a run is
 * active.
 */
export function cacheStateFromBootstrap(
  bootstrap: QuickBooksCfoCoreBootstrapResponse | null,
): CfoShellCacheState | null {
  if (!bootstrap) return null
  const status = trimStr(bootstrap.cfo_analytics_status)
  switch (cfoAnalyticsState(bootstrap)) {
    case 'ready':
      return { tone: 'ready', label: 'Live · cached', title: 'CFO analytics ready' }
    case 'stale_showing_previous':
      return {
        tone: 'idle',
        label: 'Last completed update',
        title: status || 'Showing the last completed CFO analytics. A report update is required to include the latest QuickBooks data.',
      }
    case 'binding_setup': {
      const setup = bootstrap.binding_setup
      if (setup?.state === 'reporting_disabled') {
        return {
          tone: 'idle',
          label: 'CFO reporting disabled',
          title: trimStr(setup.message) || 'CFO reporting is disabled for this site.',
        }
      }
      // A layout that needs review waits on an editor, never on syncing: it is
      // never labelled as setting up, whatever the attention flag says.
      if (setup?.state === 'layout_needs_review') {
        return {
          tone: 'failed',
          label: 'Layout needs review',
          title: trimStr(setup.message) || status || 'The statement layout must be fixed and published before CFO reports can be set up.',
        }
      }
      return {
        tone: setup?.needs_attention ? 'failed' : 'idle',
        label: setup?.needs_attention
          ? 'Needs attention'
          : setup?.state === 'ready_to_set_up' ? 'Ready to set up' : 'Setting up',
        title: trimStr(setup?.message) || status || 'CFO reports are not set up for this workbook yet.',
      }
    }
    case 'needs_report_update':
    default:
      return {
        tone: 'idle',
        label: 'Update required',
        title: status || 'A report update is required to prepare CFO analytics.',
      }
  }
}

/**
 * Warm-cache / readiness probe used by every interior page. Fetches
 * `getQuickBooksCfoCoreBootstrap` in parallel with page data so the topbar
 * cache pill and stale notices stay consistent without a second caching layer.
 */
export function useCfoBootstrap(appApi: ServerApiClient, workbookId: string | null) {
  const [bootstrap, setBootstrap] = useState<QuickBooksCfoCoreBootstrapResponse | null>(null)
  const [bootstrapError, setBootstrapError] = useState<any | null>(null)
  const [bootstrapLoading, setBootstrapLoading] = useState(() => Boolean(trimStr(workbookId)))

  useEffect(() => {
    let cancelled = false
    const wb = trimStr(workbookId)
    if (!wb) {
      setBootstrap(null)
      setBootstrapError(null)
      setBootstrapLoading(false)
      return
    }

    const now = Date.now()
    const cached = cfoBootstrapCache.get(wb)
    if (cached && cached.value && now - cached.updatedAt < CFO_BOOTSTRAP_CACHE_TTL_MS) {
      setBootstrap(cached.value)
      setBootstrapError(null)
      setBootstrapLoading(false)
      return
    }

    if (cached?.value) {
      setBootstrap(cached.value)
    }
    setBootstrapError(cached?.error || null)
    setBootstrapLoading(!cached?.value)

    loadCfoBootstrapCached(appApi, wb)
      .then((boot) => {
        if (!cancelled) {
          setBootstrap(boot)
          setBootstrapError(cfoBootstrapCache.get(wb)?.error || null)
        }
      })
      .finally(() => {
        if (!cancelled) setBootstrapLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appApi, workbookId])

  // No timer here. The bootstrap GET starts no work, so re-reading it on a
  // clock (it used to re-fetch every 5s whenever status prose said
  // "prepare") polls forever when nothing is running. The materialization
  // status hook polls only while a durable run is active and announces its
  // completion; re-read once then.
  useEffect(() => {
    const wb = trimStr(workbookId)
    if (!wb || typeof window === 'undefined') return
    let cancelled = false
    const onComplete = (event: Event) => {
      const detail = (event as CustomEvent<{ workbookId?: string; source?: string }>).detail
      if (trimStr(detail?.workbookId) !== wb || detail?.source === 'bootstrap') return
      const cached = cfoBootstrapCache.get(wb)
      if (cached) cached.updatedAt = 0
      loadCfoBootstrapCached(appApi, wb).then((boot) => {
        if (!cancelled) {
          setBootstrap(boot)
          setBootstrapError(cfoBootstrapCache.get(wb)?.error || null)
        }
      })
    }
    window.addEventListener('fg:cfo-materialization-complete', onComplete)
    return () => {
      cancelled = true
      window.removeEventListener('fg:cfo-materialization-complete', onComplete)
    }
  }, [appApi, workbookId])

  const cacheState = useMemo(() => cacheStateFromBootstrap(bootstrap), [bootstrap])
  const analyticsReady = bootstrap?.cfo_analytics_ready

  return { bootstrap, cacheState, analyticsReady, bootstrapLoading, bootstrapError }
}
